import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from '@syncfusion/ej2-angular-buttons';
import { TextBoxAllModule } from '@syncfusion/ej2-angular-inputs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AlertPopupComponent } from '../../../components/alert-popup/alert-popup.component';
import { LoginService } from '../../../../src/app/service/login.service';
import { UserService } from '../../../../src/app/service/user.service';
import { AuthBrandPanelComponent } from '../../../../src/app/shared/auth-brand-panel/auth-brand-panel.component';

/**
 * Component for handling password reset requests by collecting a user's email and triggering a reset link workflow.
 * @component
 * @selector app-reset-password
 * @example
 *   <app-reset-password [inputProp]="sample_value1" (outputEvent)="handler()"></app-reset-password>
 * @input string email - Email address used to request a password reset link
 * @input string confirmPassword - Confirm password value maintained by the component
 * @input number userId - Logged-in user identifier loaded during initialization
 * @input { alertMessage: string, alertVisible: string } alertData - Controls the visibility and content of the alert popup
 * @method ngOnInit - Initializes the component and loads the current user ID from UserService
 * @method onSubmit(form: any) - Validates the form and submits the password reset request to the backend
 * @method closeAlert() - Clears the alert state and redirects the user to the login page
 * @method closePopup() - Redirects the user to the login page when the popup is dismissed
 * @lifecycle ngOnInit - Angular lifecycle hook used to initialize the component state
 */
@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [TextBoxAllModule, ButtonModule, CommonModule, FormsModule, AlertPopupComponent, TranslateModule, AuthBrandPanelComponent],
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.css', '../../../../src/app/shared/styles/auth-shell.css'],
})
export class ResetPasswordComponent {
  email: string = '';
  confirmPassword: string = '';
  userId: number = 0;
  alertData = { alertMessage: '', alertVisible: '' };

  constructor(private router: Router, private user: UserService, private loginService: LoginService, private translate: TranslateService) { }

  ngOnInit() {
    this.userId = this.user.getUserId();
  }

  /**
   * Submits the password reset form and requests a reset link for the provided email address.
   * @example
   * onSubmit(form)
   * undefined
   * @param {any} form - The submitted form object used to validate input before sending the reset request.
   * @returns {void} Does not return a value.
   */
  onSubmit(form: any) {
    if (form.invalid) {
      return;
    }

    const params = { email: this.email };

    this.loginService.resetPassword(params).subscribe({
      next: (response) => {
        if (response.status === 200) {
          this.alertData = { alertMessage: this.translate.instant('compliance.reset_password.reset_link_sent'), alertVisible: 'success' };
        } else {
          console.error('Unexpected response:', response);
        }
      },
      error: (error) => {
        console.error("Error while login:", error);
      }
    });
  }


  closeAlert(): void {
    this.alertData = { alertMessage: '', alertVisible: '' };
    this.router.navigate(['/login']);
  }

  closePopup(): void {
    this.router.navigate(['/login']);
  }
}
